import { type ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PanelLeftClose, PanelLeft, type LucideIcon } from 'lucide-react'
import { cn } from '../../lib/utils'

// ENG-133 — the shared three-zone sidebar skeleton + row/collapse rendering,
// extracted from the ENG-116/134 consultant Sidebar so the respondent sidebar
// reuses the exact same visual system instead of a parallel build. The consultant
// and respondent sidebars are thin configs over these parts.

export interface NavRowItem {
  to: string
  icon: LucideIcon
  label: string
  // Dot is pre-gated by the caller (module declares a style AND the badge is live).
  badge?: boolean
  badgeStyle?: 'alert' | 'amber'
  soon?: boolean
  soonLabel?: string
}

// ENG-134: active = elevation (white pill + shadow), not a colour fill — the same
// treatment tabs use, so nav and tabs read as one system.
function rowClass(collapsed: boolean) {
  return ({ isActive }: { isActive: boolean }) =>
    cn(
      'mb-0.5 flex items-center gap-2.5 rounded-xl px-2.5 py-[7px] text-sm transition-colors',
      isActive
        ? 'bg-card font-medium text-foreground shadow-[var(--shadow-card)]'
        : 'text-foreground/80 hover:bg-frost/50 hover:text-foreground',
      collapsed && 'justify-center',
    )
}

export function NavRow({ item, collapsed }: { item: NavRowItem; collapsed: boolean }) {
  const { icon: Icon, label } = item
  if (item.soon) {
    return (
      <div
        title={collapsed ? label : undefined}
        className={cn('mb-0.5 flex cursor-default items-center gap-2.5 rounded-xl px-2.5 py-[7px] text-sm text-muted-foreground/70', collapsed && 'justify-center')}
      >
        <Icon className="size-4 shrink-0" />
        {!collapsed && <span className="flex-1">{label}</span>}
        {!collapsed && item.soonLabel && (
          <span className="rounded-full border border-frost/80 bg-frost/40 px-1.5 py-px text-caption text-muted-foreground">{item.soonLabel}</span>
        )}
      </div>
    )
  }
  return (
    <NavLink to={item.to} title={collapsed ? label : undefined} className={rowClass(collapsed)}>
      <Icon className="size-4 shrink-0" />
      {!collapsed && <span className="flex-1 truncate">{label}</span>}
      {!collapsed && item.soonLabel && (
        <span className="rounded-full border border-frost/80 bg-frost/40 px-1.5 py-px text-caption text-muted-foreground">{item.soonLabel}</span>
      )}
      {!collapsed && item.badge && (
        <span className={cn('size-1.5 rounded-full', item.badgeStyle === 'alert' ? 'bg-danger' : 'bg-warning')} />
      )}
    </NavLink>
  )
}

// Desktop-only collapse toggle (tablet is a fixed rail); guarded by canCollapse.
export function CollapseToggle({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const { t } = useTranslation('admin')
  return (
    <button
      onClick={onToggle}
      title={collapsed ? t('shell.expand') : t('shell.collapse')}
      // Inset focus ring so a focused toggle reads as a clean full-width state,
      // not the browser's default outset ring poking past the dividers.
      className="hidden shrink-0 items-center justify-center border-t border-frost/60 py-2 text-muted-foreground transition-colors hover:bg-frost/40 hover:text-foreground focus-visible:outline-none focus-visible:bg-frost/40 focus-visible:text-foreground lg:flex"
    >
      {collapsed ? <PanelLeft className="size-4" /> : <PanelLeftClose className="size-4" />}
    </button>
  )
}

// The skeleton: fixed header + fixed sub-block + scrollable middle + pinned footer
// + collapse toggle. Callers own the content of each zone; the frame owns the
// wrappers that must stay identical across both sidebars. AppShell owns the
// border/radius, so no card chrome here (ENG-130).
export default function SidebarFrame({
  header,
  fixed,
  scroll,
  footer,
  collapsed,
  canCollapse = true,
  onToggleCollapse,
}: {
  header: ReactNode
  fixed?: ReactNode
  scroll?: ReactNode
  footer: ReactNode
  collapsed: boolean
  canCollapse?: boolean
  onToggleCollapse: () => void
}) {
  return (
    <div className="flex h-full flex-col">
      {header}
      {fixed}
      <div className="flex-1 overflow-y-auto">{scroll}</div>
      {footer}
      {canCollapse && <CollapseToggle collapsed={collapsed} onToggle={onToggleCollapse} />}
    </div>
  )
}
